// Settings → Home screen → Widgets: what the outside widgets need to know.
// The weather's place (looked up by name through the server's geocoder) and
// units, and which Home Assistant entities the house widget shows. Synced on
// the Settings record (`widgets`), so every Home has the same.
import { useEffect, useState } from 'react'
import { Check } from 'lucide-react'
import { api } from '@/runtime'
import type { HomeEntity, Place } from '@/db/types'
import { Skeleton } from '@/components/Skeleton'
import { cx } from '@/components/ui'
import { useAccess } from '@/state/access'
import { EntityIcon, saveWidgetConfig, useWidgetConfig } from './outsideWidgets'

const KIND_WORDS: Record<string, string> = {
  light: 'Light',
  switch: 'Switch',
  input_boolean: 'Switch',
  fan: 'Fan',
  climate: 'Heating',
  lock: 'Lock',
  cover: 'Blind or cover',
  media_player: 'Speaker or TV',
  binary_sensor: 'Sensor',
  person: 'Person',
}

/** A Home Assistant entity's kind in words, so two "Living room"s can be told apart without its id. */
const entityKind = (e: { domain: string; unit?: string }): string => {
  if (e.domain === 'sensor')
    return e.unit === '°C' || e.unit === '°F'
      ? 'Temperature'
      : e.unit === '%'
        ? 'Humidity or level'
        : 'Sensor'
  const w = KIND_WORDS[e.domain]
  if (w) return w
  const d = e.domain.replace(/_/g, ' ')
  return d.charAt(0).toUpperCase() + d.slice(1)
}
import { toggleEntity } from './widgetConfig'

const Label = ({ children }: { children: string }) => (
  <h3 className="px-1 pb-1.5 text-[13px] font-semibold text-muted">{children}</h3>
)

const chip = (on: boolean) =>
  cx(
    'rounded-full px-3.5 min-h-9 text-sm border',
    on ? 'bg-accent text-on-accent border-transparent' : 'bg-raised border-line',
  )

const placeName = (p: Place) => `${p.name}${p.admin ? `, ${p.admin}` : ''}`

const WeatherPlace = () => {
  const { place, units } = useWidgetConfig()
  const [q, setQ] = useState('')
  const [places, setPlaces] = useState<Place[] | null>(null)
  const [looking, setLooking] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const lookUp = async () => {
    const t = q.trim()
    if (t.length < 2) return
    setLooking(true)
    setProblem(null)
    try {
      setPlaces((await api.geocode(t)).places)
    } catch {
      setPlaces(null)
      setProblem('The lookup needs the server; try again when online.')
    } finally {
      setLooking(false)
    }
  }
  return (
    <section className="flex flex-col gap-2" data-testid="widget-weather-settings">
      <Label>Weather</Label>
      <p className="text-sm text-muted px-1" data-testid="widget-place">
        {place ? `For ${place.name}.` : 'No place yet: type a town and pick it.'}
      </p>
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          void lookUp()
        }}
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="A town, e.g. Leeds"
          className="flex-1 min-w-0"
          enterKeyHint="search"
          aria-label="Weather place"
          data-testid="widget-place-input"
        />
        <button type="submit" className={chip(false)} disabled={looking}>
          Find
        </button>
      </form>
      {looking && <Skeleton className="h-12" />}
      {problem && <p className="text-xs text-faint px-1">{problem}</p>}
      {places && !looking && (
        <ul className="group-list">
          {places.length === 0 && (
            <li className="px-3.5 min-h-12 flex items-center text-sm text-faint">Nothing by that name.</li>
          )}
          {places.map((p) => (
            <li key={`${p.latitude},${p.longitude}`}>
              <button
                type="button"
                onClick={() => {
                  void saveWidgetConfig({
                    place: { name: placeName(p), latitude: p.latitude, longitude: p.longitude },
                  })
                  setPlaces(null)
                  setQ('')
                }}
                className="w-full text-left px-3.5 min-h-12 flex items-center gap-3"
              >
                <span className="flex-1 truncate">{placeName(p)}</span>
                <span className="text-xs text-faint shrink-0">{p.country}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-1.5" role="radiogroup" aria-label="Temperature">
        {(['celsius', 'fahrenheit'] as const).map((u) => (
          <button
            key={u}
            type="button"
            role="radio"
            aria-checked={units === u}
            onClick={() => void saveWidgetConfig({ units: u })}
            className={chip(units === u)}
          >
            {u === 'celsius' ? '°C' : '°F'}
          </button>
        ))}
      </div>
    </section>
  )
}

/** Hundreds of entities: a filter, a tick for each, and Chosen to see the chosen ones alone. */
const HouseEntities = () => {
  const { house } = useWidgetConfig()
  const [all, setAll] = useState<{ configured: boolean; entities: HomeEntity[] } | null>(null)
  const [failed, setFailed] = useState(false)
  const [q, setQ] = useState('')
  const [chosenOnly, setChosenOnly] = useState(false)
  useEffect(() => {
    let live = true
    api
      .homeStates([])
      .then((r) => {
        if (live) setAll(r)
      })
      .catch(() => {
        if (live) setFailed(true)
      })
    return () => {
      live = false
    }
  }, [])
  const chosen = new Set(house)
  const needle = q.trim().toLowerCase()
  const shown = (all?.entities ?? [])
    .filter((e) => !chosenOnly || chosen.has(e.id))
    .filter((e) => !needle || e.name.toLowerCase().includes(needle) || e.id.toLowerCase().includes(needle))
    .slice(0, 80)
  return (
    <section className="flex flex-col gap-2" data-testid="widget-house-settings">
      <Label>House</Label>
      {!all ? (
        failed ? (
          <p className="text-sm text-faint px-1">
            Home Assistant is reached through the server; try again when online.
          </p>
        ) : (
          <div className="flex flex-col gap-2" aria-busy="true">
            <Skeleton className="h-10" />
            <Skeleton className="h-32" />
          </div>
        )
      ) : !all.configured ? (
        <p className="text-sm text-faint px-1">
          Home Assistant is not set up on the server (<code>ALFIEOS_HA_URL</code> and{' '}
          <code>ALFIEOS_HA_TOKEN</code>).
        </p>
      ) : (
        <>
          <p className="text-sm text-muted px-1">
            What the House widget shows, in the order chosen. A tap there toggles lights, switches, locks and
            covers.
          </p>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter: lamp, kitchen, sensor…"
            aria-label="Filter entities"
            data-testid="widget-house-filter"
          />
          <div className="flex gap-1.5">
            <button type="button" onClick={() => setChosenOnly(false)} className={chip(!chosenOnly)}>
              All
            </button>
            <button type="button" onClick={() => setChosenOnly(true)} className={chip(chosenOnly)}>
              Chosen
            </button>
          </div>
          <ul className="group-list">
            {shown.length === 0 && (
              <li className="px-3.5 min-h-12 flex items-center text-sm text-faint">
                {chosenOnly ? 'Nothing chosen yet.' : 'Nothing matches.'}
              </li>
            )}
            {shown.map((e) => {
              const on = chosen.has(e.id)
              return (
                <li key={e.id}>
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={on}
                    onClick={() => void saveWidgetConfig({ house: toggleEntity(house, e.id) })}
                    className="w-full text-left px-3.5 min-h-12 flex items-center gap-3"
                    data-testid={`widget-house-${e.id}`}
                  >
                    <EntityIcon e={e} className="shrink-0 text-muted" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{e.name}</span>
                      {/* what kind of thing it is, in words; the entity id only on hover */}
                      <span className="block text-xs text-faint truncate" title={e.id}>
                        {entityKind(e)}
                      </span>
                    </span>
                    <span
                      className={cx(
                        'h-6 w-6 rounded-full border flex items-center justify-center shrink-0',
                        on ? 'bg-accent border-transparent text-on-accent' : 'border-line-strong',
                      )}
                      aria-hidden
                    >
                      {on && <Check size={14} />}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </>
      )}
    </section>
  )
}

/** The widgets' own choices, under the arrangement's. Online-only parts ask the server when shown. */
export const WidgetSettings = () => {
  const member = useAccess((s) => s.member)
  const admin = useAccess((s) => s.admin)
  if (member && !admin) return null
  return (
    <section className="flex flex-col gap-4" data-testid="widget-settings">
      <h2 className="px-1 -mb-2 text-[13px] font-semibold text-muted">Widgets</h2>
      <p className="text-xs text-faint px-1">
        {admin
          ? 'The weather needs a place; Lyrics and Up next follow the music. Add any of them from Home’s plus.'
          : "Weather and House need these; Lyrics and Up next follow Spotify. Add any of them from Home's plus."}
      </p>
      <WeatherPlace />
      {/* the house is the owner's */}
      {!admin && <HouseEntities />}
    </section>
  )
}
